import { Box, Link, MenuItem } from '@mui/material';
import React from 'react';

import { UtilityTrayMenuItem } from '../../types';
import { handleTabOrEscapeToLeaveSubmenu } from '../../utils/keyboardNavUtils';
import { TrayMenuItemText } from './TrayMenuItemText';

const styles = {
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    p: 0,
    whiteSpace: 'normal'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    ':visited': {
      color: 'currentColor'
    },
    // Padding must be on content, not menuItem, so that link is clickable anywhere on item
    px: 2,
    py: 1,
    width: '100%'
  }
};

interface ITrayMenuItemProps extends UtilityTrayMenuItem {
  dataTest: string;
  autoFocus?: boolean;
  dataOg?: string;
  anchorEl?: HTMLElement | null;
  firstTrayMenuItemRef?: React.RefObject<HTMLLIElement>;
  handleClose?(): void;
  onClick?(): void;
}

export const TrayMenuItem: React.FC<ITrayMenuItemProps> = ({
  dataTest,
  autoFocus,
  dataOg,
  label,
  description,
  openInNewTab,
  anchorEl,
  firstTrayMenuItemRef,
  url,
  linkComponent,
  onClick,
  handleClose,
  ...muiMenuItemProps
}) => {
  const linkRef = React.useRef<HTMLAnchorElement>(null);
  return (
    <MenuItem
      data-test={dataTest}
      data-og={dataOg}
      autoFocus={autoFocus}
      ref={autoFocus ? firstTrayMenuItemRef : undefined}
      onClick={(e) => {
        // e.isTrusted will only be true for real user clicks, not programmatic ones
        // Avoids onClick happening twice when triggered by bubbling up from link click from keyboard
        if (onClick && (!url || (url && e.isTrusted))) {
          onClick();
        }
        handleClose?.();
      }}
      onKeyDown={(e) => {
        handleTabOrEscapeToLeaveSubmenu(e.key, anchorEl);
        if (['Enter', ' '].includes(e.key) && url && linkRef?.current) {
          // If item has a URL or onClick, click inner Link/button component
          // (important for keyboard navigation & general accessibility)
          linkRef.current.click();
        }
      }}
      sx={{ ...styles.menuItem }}
      {...muiMenuItemProps}
    >
      {url ? (
        <Link
          href={url}
          ref={linkRef}
          target={openInNewTab ? '_blank' : undefined}
          underline="none"
          component={linkComponent || 'a'}
          tabIndex={-1}
          sx={styles.content}
          onClick={() => {
            handleClose?.();
          }}
        >
          <TrayMenuItemText label={label} description={description} />
        </Link>
      ) : (
        <Box sx={styles.content}>
          <TrayMenuItemText label={label} description={description} />
        </Box>
      )}
    </MenuItem>
  );
};
